/*******************************************************************************
 * Copyright (c) 2013, 2016 EclipseSource.
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ******************************************************************************/
package uk.co.nickthecoder.kojak

/**
 * A streaming parser for JSON text. The parser reports all events to a given handler.
 *
 * Reads the entire input from the given reader and parses it as JSON. The input must contain a
 * valid JSON value, optionally padded with whitespace.
 *
 * Characters are read in chunks into an input buffer of the given size. Hence, wrapping [reader]
 * in an additional `BufferedReader` likely won't improve reading performance.
 */
internal class JsonParser<O : Any, A : Any> internal constructor(
    private val handler: JsonHandler<O, A>,
    private val reader: MPPReader,
    bufferSize: Int
) {

    companion object {
        val MAX_NESTING_LEVEL = 1000
        val MIN_BUFFER_SIZE = 10
        val DEFAULT_BUFFER_SIZE = 1024
    }

    init {
        if (bufferSize <= 0) {
            throw IllegalArgumentException("bufferSize is zero or negative")
        }
        handler.parser = this
    }

    private val buffer = CharArray(bufferSize) { ' ' }

    private var bufferOffset: Int = 0
    private var index: Int = 0
    private var fill: Int = 0
    private var line: Int = 1
    private var lineOffset: Int = 0
    private var current: Char = 0.toChar()
    private var captureBuffer = StringBuilder()
    private var captureStart: Int = - 1
    private var nestingLevel: Int = 0
    private var isEOF = false


    /*
     * |                      bufferOffset
     *                        v
     * [a|b|c|d|e|f|g|h|i|j|k|l|m|n|o|p|q|r|s|t]        < input
     *                       [l|m|n|o|p|q|r|s|t|?|?]    < buffer
     *                          ^               ^
     *                       |  index           fill
     */


    /**
     * @throws IllegalArgumentException if bufferSize is invalid
     * @throws ParseException if the input is not valid JSON
     */
    internal fun parse() {
        read()
        skipWhiteSpace()
        readValue()
        skipWhiteSpace()
        if (! isEndOfText()) {
            throw error("Unexpected character")
        }
    }


    private fun readValue() {
        when (current) {
            'n' -> readNull()
            't' -> readTrue()
            'f' -> readFalse()
            '"' -> readString()
            '[' -> readArray()
            '{' -> readObject()
            '-', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9' -> readNumber()
            else -> throw expected("value")
        }
    }

    private fun readArray() {
        val array = handler.startArray()
        read()
        if (++ nestingLevel > MAX_NESTING_LEVEL) {
            throw error("Nesting too deep");
        }
        skipWhiteSpace()
        if (readChar(']')) {
            nestingLevel --
            handler.endArray(array)
            return
        }

        do {
            skipWhiteSpace()
            handler.startArrayValue(array)
            readValue()
            handler.endArrayValue(array)
            skipWhiteSpace()
        } while (readChar(','))
        if (! readChar(']')) {
            throw expected("',' or ']'")
        }
        nestingLevel --;
        handler.endArray(array)
    }

    private fun readObject() {
        val obj = handler.startObject()
        read()
        if (++ nestingLevel > MAX_NESTING_LEVEL) {
            throw error("Nesting too deep");
        }
        skipWhiteSpace()
        if (readChar('}')) {
            nestingLevel --
            handler.endObject(obj)
            return
        }
        do {
            skipWhiteSpace()
            handler.startObjectName(obj)
            val name = readName()
            handler.endObjectName(obj, name)
            skipWhiteSpace()
            if (! readChar(':')) {
                throw expected("':'")
            }
            skipWhiteSpace()
            handler.startObjectValue(obj, name)
            readValue();
            handler.endObjectValue(obj, name)
            skipWhiteSpace()
        } while (readChar(','))

        if (! readChar('}')) {
            throw expected("',' or '}'")
        }
        nestingLevel --
        handler.endObject(obj)
    }

    fun readName(): String {
        if (current != '"') {
            throw expected("name")
        }
        return readStringInternal()
    }

    private fun readNull() {
        handler.startNull()
        read()
        readRequiredChar('u')
        readRequiredChar('l')
        readRequiredChar('l')
        handler.endNull()
    }

    private fun readTrue() {
        handler.startBoolean()
        read()
        readRequiredChar('r')
        readRequiredChar('u')
        readRequiredChar('e')
        handler.endBoolean(true)
    }

    private fun readFalse() {
        handler.startBoolean()
        read()
        readRequiredChar('a')
        readRequiredChar('l')
        readRequiredChar('s')
        readRequiredChar('e')
        handler.endBoolean(false)
    }

    private fun readRequiredChar(ch: Char) {
        if (! readChar(ch)) {
            throw expected("'${ch}'")
        }
    }

    private fun readString() {
        handler.startString()
        handler.endString(readStringInternal())
    }

    private fun readStringInternal(): String {
        read()
        startCapture()
        while (current != '"') {
            if (current == '\\') {
                pauseCapture()
                readEscape()
                startCapture()
            } else if (current < ' ') {
                throw expected("valid string character")
            } else {
                read()
            }
        }
        val string = endCapture()
        read()
        return string
    }

    private fun readEscape() {
        read()
        when (current) {
            '"', '/', '\\' -> captureBuffer.append(current)
            'b' -> captureBuffer.append('\b')
            'f' -> captureBuffer.append('\u000C')
            'n' -> captureBuffer.append('\n')
            'r' -> captureBuffer.append('\r')
            't' -> captureBuffer.append('\t')
            'u' -> {
                val hexChars = CharArray(4) { ' ' }
                for (i in 0 until 4) {
                    read()
                    if (! isHexDigit()) {
                        throw expected("hexadecimal digit")
                    }
                    hexChars[i] = current
                }
                captureBuffer.append(hexChars.concatToString().toInt(radix = 16).toChar())
            }

            else -> throw expected("valid escape sequence")
        }

        read()
    }

    private val hexDigits = charArrayOf('0', '1', '2', '3', '4', '5', '6', '7', '8', '9', 'a', 'b', 'c', 'd', 'e', 'f')

    private fun readNumber() {
        handler.startNumber()
        startCapture()
        readChar('-')
        val firstDigit = current
        if (! readDigit()) {
            throw expected("digit")
        }
        if (firstDigit != '0') {
            while (readDigit()) {
            }
        }
        readFraction()
        readExponent()
        handler.endNumber(endCapture())
    }

    private fun readFraction(): Boolean {
        if (! readChar('.')) {
            return false
        }
        if (! readDigit()) {
            throw expected("digit")
        }
        while (readDigit()) {
        }
        return true
    }

    private fun readExponent(): Boolean {
        if (! readChar('e') && ! readChar('E')) {
            return false;
        }
        if (! readChar('+')) {
            readChar('-')
        }
        if (! readDigit()) {
            throw expected("digit")
        }
        while (readDigit()) {
        }
        return true
    }

    private fun readChar(ch: Char): Boolean {
        if (current != ch) {
            return false
        }
        read()
        return true
    }

    private fun readDigit(): Boolean {
        if (! isDigit()) {
            return false;
        }
        read()
        return true;
    }

    private fun skipWhiteSpace() {
        while (isWhiteSpace()) {
            read()
        }
    }

    private fun read() {
        if (index == fill) {
            if (captureStart != - 1) {
                captureBuffer.appendRange(buffer, captureStart, fill)
                captureStart = 0
            }
            bufferOffset += fill
            fill = reader.read(buffer, 0, buffer.size)
            index = 0
            if (fill == - 1) {
                current = 0.toChar()
                isEOF = true
                index ++
                return
            }
        }
        if (current == '\n') {
            line ++
            lineOffset = bufferOffset + index
        }
        current = buffer[index ++]
    }

    private fun startCapture() {
        captureStart = index - 1
    }

    private fun pauseCapture() {
        val end = if (current == 0.toChar()) index else index - 1
        captureBuffer.appendRange(buffer, captureStart, captureStart + (end - captureStart))
        captureStart = - 1
    }

    private fun endCapture(): String {
        val start = captureStart
        val end = index - 1
        captureStart = - 1
        if (captureBuffer.length > 0) {
            captureBuffer.appendRange(buffer, start, start + (end - start))
            val captured = captureBuffer.toString()
            captureBuffer.setLength(0)
            return captured
        }
        return buffer.concatToString(start, start + (end - start))
    }

    fun getLocation(): Location {
        val offset = bufferOffset + index - 1
        val column = offset - lineOffset + 1
        return Location(offset, line, column)
    }

    private fun expected(expected: String): ParseException {
        if (isEndOfText()) {
            return error("Unexpected end of input")
        }
        return error("Expected $expected")
    }

    private fun error(message: String): ParseException {
        return ParseException(message, getLocation())
    }

    private fun isWhiteSpace(): Boolean {
        return current == ' ' || current == '\t' || current == '\n' || current == '\r'
    }

    private fun isDigit(): Boolean {
        return current in '0'..'9'
    }

    private fun isHexDigit(): Boolean {
        return current in '0'..'9'
            || current in 'a'..'f'
            || current in 'A'..'F'
    }

    private fun isEndOfText(): Boolean {
        return isEOF
    }

}
